import { Button, Card, FileInput, Select, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useCsvParser } from "../../Hooks/useCsvParser";
import { adminGetLeadClientList, importNumbers } from "../../functions/api";
import {
  isValidLvNationalNumber,
  normalizeLvNumber,
} from "../../functions/phone";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
const example = require(`../../assets/example.png`);

export const ImportScreen = () => {
  const intl = useIntl();

  const [searchParams] = useSearchParams();

  const [, parseCsv] = useCsvParser();
  const [tmpCsv, setTmpCsv] = useState([]);
  const [invalidCount, setInvalidCount] = useState(0);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const executeImport = async () => {
    if (!selected || !tmpCsv.length || importing) {
      return;
    }
    setImporting(true);
    setError(null);
    try {
      const action = await importNumbers({ data: tmpCsv, project: selected });
      if (action && action.error) {
        setError(intl.formatMessage({ id: "import_failed" }));
        return;
      }
      if (action) {
        setResult(action);
        setTmpCsv([]);
      } else {
        setError(intl.formatMessage({ id: "import_failed" }));
      }
    } catch (e) {
      setError(intl.formatMessage({ id: "import_failed" }));
    } finally {
      setImporting(false);
    }
  };

  const handleFileChange = (file) => {
    setTmpCsv([]);
    setInvalidCount(0);
    setDuplicateCount(0);
    setError(null);
    setResult(null);
    if (!file) {
      return;
    }
    parseCsv(file, (data) => {
      if (data.error) {
        setError(
          data.error === "Missing required column"
            ? intl.formatMessage({ id: "import_missing_phone_header" })
            : intl.formatMessage({ id: "import_parse_error" })
        );
        return;
      }

      const seen = new Set();
      const unique = [];
      let invalid = 0;
      let duplicates = 0;

      data.forEach((item) => {
        const number = normalizeLvNumber(item.phone || item.number);
        if (!isValidLvNationalNumber(number)) {
          invalid += 1;
          return;
        }
        if (seen.has(number)) {
          duplicates += 1;
          return;
        }
        seen.add(number);
        unique.push({
          number,
          comment: item.comment ? String(item.comment) : null,
        });
      });

      setInvalidCount(invalid);
      setDuplicateCount(duplicates);
      setTmpCsv(unique);
    });
  };

  useEffect(() => {
    adminGetLeadClientList().then((x) => {
      if (!x || x.error || !Array.isArray(x)) {
        return;
      }
      setProjects(
        x.map((y) => {
          return { label: y.name, value: String(y.lrid) };
        })
      );
      const pid = searchParams.get("pid");
      if (pid) {
        setSelected(pid);
      }
    });
  }, [searchParams]);

  const skipped = result && result.skipped ? result.skipped : {};

  return (
    <Card>
      <Card withBorder>
        <Select
          style={{ marginTop: 15 }}
          description={intl.formatMessage({ id: "import_project_description" })}
          searchable
          dropdownPosition="bottom"
          value={selected}
          data={projects}
          label={intl.formatMessage({ id: "projects" })}
          onChange={(x) => {
            setSelected(x);
            setResult(null);
          }}
          withinPortal
        />
        {selected && (
          <div
            style={{
              marginTop: 10,
              marginBottom: 10,
            }}
          >
            {intl.formatMessage({ id: "import_csv_hint" })}
            <img
              src={example}
              alt={intl.formatMessage({ id: "import_example_alt" })}
              style={{ height: 85, display: "block", marginTop: 8 }}
            />
          </div>
        )}
        {selected && (
          <FileInput
            onChange={handleFileChange}
            label={intl.formatMessage({ id: "select_csv_import" })}
            placeholder={intl.formatMessage({ id: "select_csv_import" })}
            accept=".csv,.txt"
          />
        )}
        {error && (
          <Text color="red" mt={10}>
            {error}
          </Text>
        )}
        {tmpCsv && tmpCsv.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <strong>
              {intl.formatMessage({ id: "import_ready_count" })}: {tmpCsv.length}
            </strong>
            {(invalidCount > 0 || duplicateCount > 0) && (
              <div>
                {intl.formatMessage(
                  { id: "import_file_skipped" },
                  { invalid: invalidCount, duplicates: duplicateCount }
                )}
              </div>
            )}
          </div>
        )}
        {tmpCsv && tmpCsv.length > 0 && (
          <div style={{ marginTop: 20, marginBottom: 5 }}>
            <Button
              onClick={() => executeImport()}
              loading={importing}
              disabled={importing}
              style={{ backgroundColor: "dodgerblue" }}
            >
              {intl.formatMessage({ id: "import" })}
            </Button>
          </div>
        )}
        {result && (
          <div style={{ marginTop: 16 }}>
            <strong>
              {intl.formatMessage(
                { id: "import_success_count" },
                { count: result.count || 0 }
              )}
            </strong>
            <div>
              {intl.formatMessage({ id: "import_skipped_blacklist" })}:{" "}
              {skipped.blacklist || 0}
            </div>
            <div>
              {intl.formatMessage({ id: "import_skipped_pending" })}:{" "}
              {skipped.pending || 0}
            </div>
            <div>
              {intl.formatMessage({ id: "import_skipped_reached" })}:{" "}
              {skipped.reached || 0}
            </div>
            <div>
              {intl.formatMessage({ id: "import_skipped_called" })}:{" "}
              {skipped.called || 0}
            </div>
            <div>
              {intl.formatMessage({ id: "import_skipped_invalid_status" })}:{" "}
              {skipped.invalid_status || 0}
            </div>
          </div>
        )}
      </Card>
    </Card>
  );
};
